import type { DocsState } from "@/store/docs/state";
import type { Component } from "vue";
import type { DocsLanguages } from "@/models/documentation";
import { PerspectiveViewConfig } from "@/models/perspective";

/**
 * Sets the next Component to be rendered in the Docs Drawer
 * @param state - Vuex state for the Docs module.
 * @param value - New component to render
 */
export function pushDrawerComponent(state: DocsState, value: Component): void {
  state.drawerComponents.push(value);
}

/**
 * Removes the last element of the Drawer's Components array
 * @param state - Vuex state for the Docs module.
 */
export function removeLastDrawerComponent(state: DocsState): void {
  state.drawerComponents.pop();
}

/**
 * Resets the Drawer's array of Components to render in the Docs Drawer
 * @param state - Vuex state for the Docs module.
 */
export function clearDrawerComponents(state: DocsState): void {
  state.drawerComponents = [];
}

/**
 * Set the update status for the Docs Drawer
 * @param state - Vuex state for the docs module.
 * @param value - Should we load the drawer component
 */
export function setUpdate(state: DocsState, value: boolean): void {
  state.getUpdate = value || false;
}

/**
 * Set the update status for the Docs Drawer
 * @param state - Vuex state for the docs module.
 * @param value - Should we load the drawer component
 */
export function setLanguage(state: DocsState, value: DocsLanguages): void {
  state.language = value || "javascript";
}

/**
 * Sets the value of the tableConfiguration state.
 * @param state - Vuex state for the Docs module.
 * @param value - PerspectiveViewConfig value to be assigned to tableConfiguration.
 */
export function setTableConfiguration(
  state: DocsState,
  value: PerspectiveViewConfig
): void {
  state.tableConfiguration = value;
}

/**
 * Sets the value of the tableSchema state.
 * @param state - Vuex state for the Docs module.
 * @param value - Record to be assigned to tableSchema.
 */
export function setTableSchema(
  state: DocsState,
  value: Record<string, string>
): void {
  state.tableSchema = value;
}

/**
 * Sets the value of the shouldClearTable state. State used to clear the table on filter change
 * @param state - Vuex state for the Docs module.
 * @param value - Boolean value to be assigned to shouldClearTable.
 */
export function setShouldClearTable(state: DocsState, value: boolean): void {
  state.shouldClearTable = value;
}

/**
 * Sets the value of the applyConfiguration state. State used to trigger applying configuration.
 * @param state - Vuex state for the Docs module.
 * @param value - Boolean value to be assigned to applyConfiguration.
 */
export function setApplyConfiguration(state: DocsState, value: boolean): void {
  state.applyConfiguration = value;
}
